import { useEffect } from 'react'
import { Checkbox } from '@mui/material'
import {
  allAvailableEventTypes,
  EVENT_SUBTYPE_COUPLE_THERAPY,
  EVENT_SUBTYPE_INDIVIDUAL_THERAPY,
  EVENT_SUBTYPE_THERAPIST_ASSESSMENT,
  platformOptions,
  SESSION_PLATFORM_ONLINE,
  THERAPY_DISCIPLINE_OPTIONS,
  THERAPY_DISCIPLINE_PSYCHOTHERAPY,
  VISIT_EVENT_TYPES,
  VISIT_EVENT_TYPES_ARRAY,
} from 'src/consumer/constants'
import {
  buildScreeningTargetSpecialtyOptions,
  normalizeScreeningSpecialtyBucket,
} from 'src/consumer/helpers/screeningSpecialtyUtils'
import { SuggestedAssessmentsSelectMenu } from 'src/mentor/components/PrescriptionMakingPage/PrescriptionMakingPageComponents'
import { getAllRolesInBoolean } from 'src/mentor/layout/utils'
import { GenericDisplayFlex } from 'src/mentor/layout/Styles'
import {
  GenericModalHeadlineLevelTwo,
  StyledInputSelect,
} from 'src/mentor/components/MentorAvailabilityPage/MentorAvailabilityPageStyles'

// Phase 14az — role keys mirror `Security.java` on the backend; the same strings are used
// as keys in `Practitioner.teacherUnEditableFields.servicesApproved`. Hardcoding here
// (rather than importing from a constants module) avoids pulling the heavy auth-constants
// bundle into every event-form codepath.
const ROLE_DOCTOR = 'ROLE_DOCTOR'
const ROLE_THERAPIST = 'ROLE_THERAPIST'

// Phase 14bb — event types that have a clinical context (a real patient, a clinical
// track, a downstream specialty). For these types the form exposes the `discipline`
// and `targetSpecialtySlug` pickers so the support reviewer / mentor can tag the event
// with the right routing metadata. Non-clinical types (webinar / interview) skip the
// pickers — interviews are panel-based not patient-based, webinars are broadcasts.
export function isClinicalContextEventType(allAvailable, type) {
  return (
    type === allAvailable.oneOnOne ||
    type === allAvailable.screening ||
    type === allAvailable.therapySession ||
    type === allAvailable.assessment ||
    type === allAvailable.consultation
  )
}

/**
 * Phase 14be — resolve the practitioner's KYC-approved disciplines from their profile.
 * Mirrors the backend `getEffectiveApprovedTherapyDisciplines()` fallback to
 * [psychotherapy] when the bean has nothing on file. Exposed as a named export so the
 * SAVE-time discipline guard in EventTypeCreationForm can apply the same allow-list as
 * the form's render-time auto-correction effect, with no risk of the two diverging.
 */
export function approvedDisciplineCodesForPractitioner(practitionerProfile) {
  const raw = practitionerProfile?.teacherUnEditableFields?.approvedTherapyDisciplines
  return Array.isArray(raw) && raw.length > 0 ? raw : [THERAPY_DISCIPLINE_PSYCHOTHERAPY]
}

/**
 * Phase 14az — true iff the practitioner is approved (via KYC) to deliver services as the
 * given role. Reads from the canonical `teacherUnEditableFields.servicesApproved` map
 * keyed by ROLE_*, mirroring the backend's `hasServiceRole()` check in
 * PractitionerSupportBaseController. An entry with `enabled=false` counts as not
 * approved (the bean defaults to false until ops flips the toggle in the support portal).
 */
function isPractitionerApprovedForRole(practitionerProfile, roleKey) {
  const services = practitionerProfile?.teacherUnEditableFields?.servicesApproved
  if (!services || typeof services !== 'object') return false
  const detail = services[roleKey]
  if (!detail) return false
  // ServiceDetails.enabled defaults to false on the bean; only count truthy values.
  return detail.enabled !== false
}

export default function EventTypeConfiguration({
  basicEventObject,
  setBasicEventObject,
  displayNamesOfEventTypes,
  mentorProfileData,
  forceShowDoctorEventTypes,
  forceShowTherapistEventTypes,
  changeLocationBasedOnMentorOrClinicLocation,
  isDisabled,
}) {
  // Phase 14az — Type options now derive from the practitioner's own
  // `teacherUnEditableFields.servicesApproved` map (the canonical KYC-approved services
  // map on the Practitioner bean) instead of `getAllRolesInBoolean()` which reads the
  // CURRENT user's authorities. The previous logic broke in the support portal where the
  // current user is a support agent (not the practitioner being edited) — the agent's
  // ROLE_DOCTOR / live-services flags don't say anything about whether the therapist
  // they're editing is approved to run therapy sessions or doctor consultations.
  //
  // Falls back to the prior `services.live` / `allowedToPrescribe + isDoctor` heuristics
  // ONLY when servicesApproved isn't hydrated on the profile (legacy data shapes that
  // haven't been backfilled). The `forceShowDoctorEventTypes` / `forceShowTherapistEventTypes`
  // override props are kept for callers that need to bypass approval checks (e.g. an
  // ops-only screen that intentionally shows every option).
  const servicesApprovedHydrated = Boolean(
    mentorProfileData?.teacherUnEditableFields?.servicesApproved,
  )
  const showDoctorTypes =
    Boolean(forceShowDoctorEventTypes) ||
    isPractitionerApprovedForRole(mentorProfileData, ROLE_DOCTOR) ||
    (!servicesApprovedHydrated &&
      mentorProfileData?.allowedToPrescribe &&
      getAllRolesInBoolean().isDoctor)
  const showTherapistTypes =
    Boolean(forceShowTherapistEventTypes) ||
    isPractitionerApprovedForRole(mentorProfileData, ROLE_THERAPIST) ||
    (!servicesApprovedHydrated && mentorProfileData?.services?.live)

  // Phase 14az / 14bb / 14be — derive the practitioner's KYC-approved therapy
  // disciplines once at render time so the type-change handler (preselects discipline
  // whenever switching INTO a clinical event type) and the discipline picker block
  // below can reuse the same allow-list. The named export is shared with the SAVE-
  // time discipline guard in EventTypeCreationForm so the two paths can never
  // diverge on what counts as "approved".
  const approvedDisciplineCodes = approvedDisciplineCodesForPractitioner(mentorProfileData)
  const defaultDisciplineForPractitioner = approvedDisciplineCodes[0]

  // Phase 14bb — the discipline + targetSpecialtySlug pickers used to be gated to
  // therapySession and screening respectively. They're now exposed for every clinical
  // event type (oneOnOne / screening / therapySession / assessment / consultation).
  // Both still auto-hide when the practitioner has nothing meaningful to pick — single-
  // discipline practitioners get a read-only chip instead of a 1-option dropdown, and
  // practitioners with no targetSpecialties on file see no specialty UI at all.
  const isClinicalContextType = isClinicalContextEventType(
    allAvailableEventTypes,
    basicEventObject?.type,
  )
  const disciplineOptions = THERAPY_DISCIPLINE_OPTIONS.filter((opt) =>
    approvedDisciplineCodes.includes(opt.code),
  )
  const showDisciplineSection = isClinicalContextType && disciplineOptions.length > 0
  const showDisciplinePicker = showDisciplineSection && disciplineOptions.length > 1
  const onlyDiscipline = disciplineOptions.length === 1 ? disciplineOptions[0] : null
  // Read prefers the new `discipline` field, falls back to deprecated `therapyDiscipline`
  // (Phase 14ba safe-rename) so in-flight form state from older clients still resolves.
  const currentDiscipline =
    basicEventObject?.discipline ||
    basicEventObject?.therapyDiscipline ||
    defaultDisciplineForPractitioner

  // The first entry of buildScreeningTargetSpecialtyOptions is always the "Psychiatry
  // (Default)" sentinel. Show the picker only when there's at least one real specialty
  // on the practitioner — otherwise the dropdown collapses to just the default and adds
  // no value.
  const targetSpecialtyOptions = buildScreeningTargetSpecialtyOptions(
    mentorProfileData?.targetSpecialties || [],
  )
  const hasTargetSpecialties = targetSpecialtyOptions.length > 1
  const showTargetSpecialtySection = isClinicalContextType && hasTargetSpecialties
  const isScreeningType = basicEventObject?.type === allAvailableEventTypes.screening

  // Phase 14bc — force-correct the form's discipline value when the persisted event
  // carries a discipline the practitioner is no longer approved for. Real-world case:
  // a therapy-session was created when the practitioner had `psychotherapy` approved,
  // ops later flipped them to nutrition-consultation only, the saved event still
  // carries `psychotherapy` in its therapyDiscipline field. The previous read-only
  // chip rendered the practitioner's approved label ("Nutrition Consultation") while
  // the form state silently kept `psychotherapy` — save then 400'd with "not approved
  // for therapy discipline: psychotherapy. Approved disciplines: [nutrition-
  // consultation]". Auto-correcting here on render means the next save lands on the
  // discipline the practitioner is actually approved for, with the dropdown below
  // showing the corrected value transparently to the operator. Idempotent — once the
  // corrected value matches an entry in approvedDisciplineCodes, the effect short-
  // circuits and doesn't re-fire.
  useEffect(() => {
    if (!isClinicalContextType) return
    if (!defaultDisciplineForPractitioner) return
    const current = basicEventObject?.discipline || basicEventObject?.therapyDiscipline
    if (current && approvedDisciplineCodes.includes(current)) return
    setBasicEventObject((prev) => ({
      ...prev,
      discipline: defaultDisciplineForPractitioner,
      therapyDiscipline: defaultDisciplineForPractitioner,
    }))
    // approvedDisciplineCodes is a fresh array on each render, so we depend on its
    // joined-string identity to keep the effect from spinning every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isClinicalContextType,
    basicEventObject?.discipline,
    basicEventObject?.therapyDiscipline,
    approvedDisciplineCodes.join('|'),
    defaultDisciplineForPractitioner,
  ])

  return (
    <>
      {basicEventObject?.type !== allAvailableEventTypes?.webinar && (
        <div>
          <GenericModalHeadlineLevelTwo>Type</GenericModalHeadlineLevelTwo>
          <StyledInputSelect
            value={basicEventObject?.type}
            onChange={(e) => {
              const nextType = e.target.value
              const nextIsClinical = isClinicalContextEventType(allAvailableEventTypes, nextType)
              setBasicEventObject((prev) => {
                const next = {
                  ...prev,
                  type: nextType,
                  visitEventType: VISIT_EVENT_TYPES.regular,
                }
                // Phase 14bb — preset discipline whenever switching INTO a clinical
                // event type (was scoped to therapySession only). Without this the
                // backend's getEffectiveDiscipline() fallback to PSYCHOTHERAPY would be
                // wrong for nutrition-only practitioners, and single-discipline
                // practitioners (who don't see a picker) would have no way for the
                // correct discipline to land on the saved event. Writes BOTH fields —
                // `discipline` (new canonical) and `therapyDiscipline` (deprecated,
                // read-fallback) — so the payload survives mixed client/server versions
                // during the Phase 14ba safe-rename window.
                if (nextIsClinical) {
                  const presetDiscipline =
                    prev.discipline ||
                    prev.therapyDiscipline ||
                    defaultDisciplineForPractitioner
                  next.discipline = presetDiscipline
                  next.therapyDiscipline = presetDiscipline
                }
                if (nextType === allAvailableEventTypes.therapySession) {
                  next.subtype = prev.subtype || EVENT_SUBTYPE_INDIVIDUAL_THERAPY
                }
                if (nextType === allAvailableEventTypes.screening) {
                  next.screeningAllowedIfPossible =
                    prev.screeningAllowedIfPossible !== undefined
                      ? prev.screeningAllowedIfPossible
                      : true
                }
                return next
              })
            }}
            disabled={isDisabled}
          >
            <option value={allAvailableEventTypes.oneOnOne}>
              {displayNamesOfEventTypes?.[allAvailableEventTypes.oneOnOne]}
            </option>
            <option value={allAvailableEventTypes.screening}>
              {displayNamesOfEventTypes?.[allAvailableEventTypes.screening]}
            </option>
            <option value={allAvailableEventTypes.interview}>
              {displayNamesOfEventTypes?.[allAvailableEventTypes.interview]}
            </option>
            {showDoctorTypes && (
              <option value={allAvailableEventTypes.consultation}>
                {displayNamesOfEventTypes?.[allAvailableEventTypes.consultation]}
              </option>
            )}
            {showTherapistTypes && (
              <>
                <option value={allAvailableEventTypes.therapySession}>
                  {displayNamesOfEventTypes?.[allAvailableEventTypes.therapySession]}
                </option>
                <option value={allAvailableEventTypes.assessment}>
                  {displayNamesOfEventTypes?.[allAvailableEventTypes.assessment]}
                </option>
              </>
            )}
          </StyledInputSelect>
        </div>
      )}

      {/* Phase 14bb — screening-specific note kept screening-only. */}
      {isScreeningType && (
        <div>
          Note: Screening events should be offline and associated to a clinic. Patients in clinic
          can opt to go through this step before consulting a psychiatrist.
        </div>
      )}

      {/*
        Phase 14bb / 14bc — Discipline picker. `discipline` (formerly `therapyDiscipline`
        — renamed in Phase 14ba) tags the event with a clinical track. Exposed for every
        clinical event type (oneOnOne / screening / therapySession / assessment /
        consultation). Source-of-truth is the practitioner's KYC-approved disciplines
        (teacherUnEditableFields.approvedTherapyDisciplines).

        14bc: ALWAYS render a dropdown (not a read-only chip), even when the practitioner
        has only one approved discipline. The previous read-only chip variant rendered
        the practitioner's approved-discipline label while the form state silently kept
        a stale value from a legacy event — operator pressed Save and got a server-side
        rejection. Showing the actual form value through a real dropdown (disabled when
        single-option, since there's nothing to switch to) keeps the UI honest about
        what's about to be saved, and pairs with the auto-correction effect above that
        forces stale values into the approved set on render.
       */}
      {showDisciplineSection && (
        <div style={{ marginTop: 12 }}>
          <GenericModalHeadlineLevelTwo>Discipline</GenericModalHeadlineLevelTwo>
          <StyledInputSelect
            value={currentDiscipline}
            onChange={(e) =>
              // Phase 14ba — write both fields so the payload survives mixed
              // client/server versions during the safe-rename window.
              setBasicEventObject((prev) => ({
                ...prev,
                discipline: e.target.value,
                therapyDiscipline: e.target.value,
              }))
            }
            disabled={isDisabled || !showDisciplinePicker}
          >
            {disciplineOptions.map((opt) => (
              <option key={opt.code} value={opt.code}>
                {opt.label}
              </option>
            ))}
          </StyledInputSelect>
          <div style={{ marginTop: 4, fontSize: 12, color: '#6b7280' }}>
            {showDisciplinePicker
              ? 'Tags the clinical track for this event. Drives EMR section sets on therapy reports and downstream cohort filtering. Locked at KYC — contact ops to add another discipline.'
              : `Locked at KYC — ${onlyDiscipline?.label || 'this discipline'} is the only one approved for this practitioner. Contact ops to add another discipline.`}
          </div>
        </div>
      )}

      {/*
        Phase 14bb — Target specialty picker. `targetSpecialtySlug` tags the event for a
        downstream patient cohort (psychiatry / cardiology / ...). Used to be exposed
        only on screening events (where it routes screened patients to the right
        specialist), now exposed for every clinical event type so the support team can
        also tag therapy-sessions / consultations / assessments with the cohort they
        serve. Source-of-truth is the practitioner's mentorProfileData.targetSpecialties
        — when empty the picker auto-hides. The backend field already exists on every
        event type's EventType bean (with @Field("screeningType") for legacy mapping);
        we just lift the gate on the UI side.
       */}
      {showTargetSpecialtySection && (
        <div style={{ marginTop: 12 }}>
          <GenericModalHeadlineLevelTwo>Target specialty</GenericModalHeadlineLevelTwo>
          <StyledInputSelect
            value={normalizeScreeningSpecialtyBucket(
              basicEventObject?.targetSpecialtySlug || basicEventObject?.screeningType || '',
            )}
            onChange={(e) =>
              setBasicEventObject((prev) => ({
                ...prev,
                targetSpecialtySlug: e.target.value,
              }))
            }
            disabled={isDisabled}
          >
            {targetSpecialtyOptions.map((opt) => (
              <option key={opt.slug === '' ? 'default' : opt.slug} value={opt.slug}>
                {opt.title}
              </option>
            ))}
          </StyledInputSelect>
          <div style={{ marginTop: 4, fontSize: 12, color: '#6b7280' }}>
            {isScreeningType
              ? 'Routes a screened patient to the matching downstream specialist.'
              : 'Tags this event for the matching patient cohort. Used for matching, filtering, and downstream routing.'}
          </div>
        </div>
      )}

      {/* Phase 14bb — screening-specific "allow when possible" toggle stays screening-
          only; it's a scheduling-policy bit, not a clinical-tagging bit, so it doesn't
          generalise the way the discipline + targetSpecialtySlug pickers do. */}
      {isScreeningType && (
        <GenericDisplayFlex style={{ marginTop: 12 }}>
          <div>Allow screening when scheduling permits</div>
          <Checkbox
            size="small"
            disabled={isDisabled}
            sx={{
              padding: 0,
              marginLeft: 1,
              '&.Mui-checked': { color: 'blue' },
            }}
            checked={basicEventObject?.screeningAllowedIfPossible !== false}
            onChange={(event) => {
              const { checked } = event.target
              setBasicEventObject((prev) => ({
                ...prev,
                screeningAllowedIfPossible: checked,
              }))
            }}
          />
        </GenericDisplayFlex>
      )}

      {/* Phase 14bb — therapy-format (subtype) picker stays therapy-session-only;
          individual / couple / therapist-assessment is a session-shape question that's
          inherent to therapy events, distinct from the discipline-tagging above. */}
      {basicEventObject?.type === allAvailableEventTypes.therapySession && (
        <div style={{ marginTop: 12 }}>
          <GenericModalHeadlineLevelTwo>Therapy format</GenericModalHeadlineLevelTwo>
          <StyledInputSelect
            value={basicEventObject?.subtype || EVENT_SUBTYPE_INDIVIDUAL_THERAPY}
            onChange={(e) =>
              setBasicEventObject((prev) => ({ ...prev, subtype: e.target.value }))
            }
            disabled={isDisabled}
          >
            <option value={EVENT_SUBTYPE_INDIVIDUAL_THERAPY}>Individual therapy</option>
            <option value={EVENT_SUBTYPE_COUPLE_THERAPY}>Couple therapy</option>
            <option value={EVENT_SUBTYPE_THERAPIST_ASSESSMENT}>Therapist assessment</option>
          </StyledInputSelect>
        </div>
      )}

      {basicEventObject?.type === allAvailableEventTypes.consultation && (
        <div>
          <GenericModalHeadlineLevelTwo>Event Visit Type</GenericModalHeadlineLevelTwo>
          <StyledInputSelect
            value={basicEventObject?.visitEventType || ''}
            onChange={(e) => {
              setBasicEventObject((prev) => ({ ...prev, visitEventType: e.target.value }))
            }}
            disabled={isDisabled}
          >
            <option value="" hidden>
              None
            </option>
            {VISIT_EVENT_TYPES_ARRAY?.map((item) => {
              return (
                <option key={item?.id} value={item?.id}>
                  {item?.title}
                </option>
              )
            })}
          </StyledInputSelect>
        </div>
      )}

      {basicEventObject?.type === allAvailableEventTypes.assessment && (
        <SuggestedAssessmentsSelectMenu
          isNotCreatable
          selectedAssessments={basicEventObject?.psychAssessmentIds}
          setSelectedAssessments={(value) => {
            setBasicEventObject((prev) => ({ ...prev, psychAssessmentIds: value }))
          }}
          searchApiSignature={`/api/secure/teacher/psych-assessment/search`}
          customPlaceholder="Search assessments to attach to this event..."
          doctorSpecializations={mentorProfileData?.specializedCategories}
          disabled={isDisabled}
        />
      )}

      <div>
        <GenericModalHeadlineLevelTwo>Meeting Source(Platform)</GenericModalHeadlineLevelTwo>
        <StyledInputSelect
          value={basicEventObject?.meetingSrc || SESSION_PLATFORM_ONLINE}
          onChange={(e) => {
            const nextMeetingSrc = e.target.value || SESSION_PLATFORM_ONLINE
            setBasicEventObject((prev) => ({ ...prev, meetingSrc: nextMeetingSrc }))
            changeLocationBasedOnMentorOrClinicLocation(
              basicEventObject?.location,
              basicEventObject?.contextId,
            )
          }}
          disabled={isDisabled}
        >
          {platformOptions?.map((item) => {
            return (
              <option key={item?.id} value={item?.id}>
                {item?.name}
              </option>
            )
          })}
        </StyledInputSelect>
      </div>
    </>
  )
}
